import React, { useState, useEffect } from 'react';
import { Plus, FileText, Layout, PenTool, AlertCircle, Trash2, Edit } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { TestPrepDocumentService } from '../../types';
import AddDocumentServiceModal from './Modals/AddDocumentServiceModal';

const DocumentServices: React.FC = () => {
    const [services, setServices] = useState<TestPrepDocumentService[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState<TestPrepDocumentService | undefined>(undefined);

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('testprep_document_services')
                .select(`
                    *,
                    student:testprep_students (
                        first_name,
                        last_name
                    )
                `)
                .order('deadline', { ascending: true });

            if (error) throw error;
            setServices(data || []);
        } catch (error) {
            console.error('Error fetching document services:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('testprep_document_services')
                .delete()
                .eq('id', id);
            if (error) throw error;
            fetchServices();
        } catch (error) {
            console.error('Error deleting service:', error);
            alert('Silinirken hata oluştu.');
        }
    };

    const handleStatusUpdate = async (id: string, newStatus: string) => {
        try {
            const { error } = await supabase
                .from('testprep_document_services')
                .update({ status: newStatus, updated_at: new Date().toISOString() })
                .eq('id', id);

            if (error) throw error;

            // Optimistic update
            setServices(services.map(s => s.id === id ? { ...s, status: newStatus as any } : s));
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Durum güncellenirken hata oluştu.');
        }
    };

    const togglePaymentStatus = async (service: TestPrepDocumentService, method?: string) => {
        try {
            const newIsPaid = method ? true : !service.is_paid;
            const { error } = await supabase
                .from('testprep_document_services')
                .update({ 
                    is_paid: newIsPaid,
                    payment_method: method || (newIsPaid ? (service.payment_method || 'Paid') : null),
                    updated_at: new Date().toISOString()
                })
                .eq('id', service.id);

            if (error) throw error;

            setServices(services.map(s => s.id === service.id ? { 
                ...s, 
                is_paid: newIsPaid, 
                payment_method: method || (newIsPaid ? (service.payment_method || 'Paid') : null) 
            } : s));
        } catch (error) {
            console.error('Error updating payment status:', error);
            alert('Ödeme durumu güncellenirken hata oluştu.');
        }
    };

    const StatusSelect = ({ status, id }: { status: string, id: string }) => {
        const getStatusColor = (s: string) => {
            switch (s) {
                case 'brief_received': return 'bg-gray-100 text-gray-700';
                case 'draft_prepared': return 'bg-blue-100 text-blue-700';
                case 'revision_expected': return 'bg-orange-100 text-orange-700';
                case 'completed': return 'bg-green-100 text-green-700';
                default: return 'bg-gray-100 text-gray-700';
            }
        };

        return (
            <select
                value={status}
                onChange={(e) => handleStatusUpdate(id, e.target.value)}
                className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${getStatusColor(status)}`}
            >
                <option value="brief_received">Brief Alındı</option>
                <option value="draft_prepared">Taslak Hazır</option>
                <option value="revision_expected">Revizyon Bekleniyor</option>
                <option value="completed">Tamamlandı</option>
            </select>
        );
    };

    const getTypeIcon = (types: string[] | string) => {
        const typeArray = Array.isArray(types) ? types : [types];
        return (
            <div className="flex space-x-1">
                {typeArray.includes('cv') && <FileText className="w-4 h-4 text-purple-600" />}
                {typeArray.includes('portfolio') && <Layout className="w-4 h-4 text-orange-600" />}
                {typeArray.includes('motivation_letter') && <PenTool className="w-4 h-4 text-blue-600" />}
            </div>
        );
    };

    const getTypeText = (types: string[] | string) => {
        const typeArray = Array.isArray(types) ? types : [types];
        if (typeArray.length === 0) return '-';
        return typeArray.map(t => {
            switch (t) {
                case 'cv': return 'CV';
                case 'portfolio': return 'Portfolyo';
                case 'motivation_letter': return 'Motivasyon Mektubu';
                default: return t;
            }
        }).join(', ');
    };

    const getDaysRemaining = (deadline?: string) => {
        if (!deadline) return null;
        const now = new Date();
        const due = new Date(deadline);
        const diffTime = due.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Doküman Hizmetleri</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Portfolyo, CV ve Motivasyon Mektubu süreç takibi
                    </p>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => { setEditingService(undefined); setShowModal(true); }}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Hizmet</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
            ) : (
                // LIST VIEW (Excel-like)
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium">
                                <tr>
                                    <th className="px-6 py-4">Öğrenci</th>
                                    <th className="px-6 py-4">Hizmet Türü</th>
                                    <th className="px-6 py-4">Durum</th>
                                    <th className="px-6 py-4">Ücret</th>
                                    <th className="px-6 py-4">Şirket</th>
                                    <th className="px-6 py-4 text-center">Ödeme</th>
                                    <th className="px-6 py-4">Teslim Tarihi</th>
                                    <th className="px-6 py-4">Notlar</th>
                                    <th className="px-6 py-4 text-right">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                                {services.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                                            Kayıt bulunamadı.
                                        </td>
                                    </tr>
                                ) : (
                                    services.map(service => {
                                        const daysLeft = getDaysRemaining(service.deadline);
                                        const isUrgent = daysLeft !== null && daysLeft <= 3 && service.status !== 'completed';

                                        return (
                                            <tr key={service.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                                    {service.student?.first_name} {service.student?.last_name}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-2">
                                                        {getTypeIcon(service.service_type)}
                                                        <span>{getTypeText(service.service_type)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <StatusSelect status={service.status} id={service.id} />
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                                                    {service.price ? `${service.price} ${service.currency || 'TRY'}` : '-'}
                                                </td>
                                                <td className="px-6 py-4 text-xs text-gray-600 dark:text-gray-400">
                                                    {service.company_entity || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <select
                                                        value={service.is_paid ? (service.payment_method || 'Paid') : 'pending'}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val === 'pending') {
                                                                togglePaymentStatus(service, undefined);
                                                            } else {
                                                                togglePaymentStatus(service, val);
                                                            }
                                                        }}
                                                        className={`px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer border-0 outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${service.is_paid
                                                            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'}`}
                                                    >
                                                        <option value="pending">Bekliyor</option>
                                                        <optgroup label="Nakit">
                                                            <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                                            <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                                            <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                                            <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                                            <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                                            <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                                            <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                                        </optgroup>
                                                        <optgroup label="Kredi Kartı">
                                                            <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                                            <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                                        </optgroup>
                                                        <optgroup label="Şirket">
                                                            <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                                            <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                                        </optgroup>
                                                        <option value="havale">Havale/EFT</option>
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center space-x-2">
                                                        {service.deadline ? (
                                                            <>
                                                                <span className={`${isUrgent ? 'text-red-600 font-bold' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                    {new Date(service.deadline).toLocaleDateString('tr-TR')}
                                                                </span>
                                                                {isUrgent && (
                                                                    <div title={`${daysLeft} gün kaldı!`} className="text-red-500 animate-pulse">
                                                                        <AlertCircle className="w-4 h-4" />
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                                    {service.notes || '-'}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        <button
                                                            onClick={() => { setEditingService(service); setShowModal(true); }}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(service.id)}
                                                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showModal && (
                <AddDocumentServiceModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onSave={fetchServices}
                    editService={editingService}
                />
            )}
        </div>
    );
};

export default DocumentServices;
